import { Dropdown, DropdownMenuDivider, DropdownMenuItem } from '@/components/UI/Dropdown';
import Tooltip from '@/components/UI/Tooltip';
import { defaultPromiseErrorHandler, successToast } from '@/helpers';
import { _isNil, _isNotEmpty, _noop, _notNil } from '@/littledash';
import { modalAction } from '@/utils/modal';
import { type FC, type RefObject, useLayoutEffect, useRef } from 'react';
import { RiArrowDropDownFill, RiArrowLeftRightLine } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import type {
  DataTableColumn,
  DataTableColumnGroup,
  DataTableColumnGroupExpansionAction,
  DataTableColumnMoveRequest,
  DataTableDimensions,
} from '@/components/UI/DataTableV2/DataTable.model';
import { isColumnExpanded } from '@/components/UI/DataTableV2/DataTable.util';
import { useDataTable } from '../DataTableProvider';
import styles from '../DataTableV2.module.scss';
import { DataTableColumnTypeSymbol } from './DataTableColumnTypeSymbol';
import { useGroupHeaderScroll } from '../hooks/useGroupHeaderScroll';
import { SampleHeaderContent } from '@/components/UI/DataTableV2/headers/SampleHeaderContent';
import { renderParentColumnTypeSymbolTooltip } from '@/components/UI/DataTableV2/headers/columnTypeSymbolTooltip';
import { useTruncationTooltip } from '@/components/UI/DataTableV2/hooks/useTruncationTooltip';

interface DataTableGroupHeaderProps {
  group: DataTableColumnGroup;
  visibleColspan: number;
  startOffset: number;
  dimensions: DataTableDimensions;
  scrollElementRef: RefObject<HTMLElement | null>;
  pinnedColumnWidth: number;
  testId?: string;
}

export const DataTableGroupHeader: FC<DataTableGroupHeaderProps> = ({
  group,
  visibleColspan,
  startOffset,
  dimensions,
  scrollElementRef,
  pinnedColumnWidth,
  testId = 'datatable-group-header',
}) => {
  const { dataTableService } = useDataTable();
  const width = visibleColspan * dimensions.cellWidth;
  const { openModal } = modalAction(useDispatch());
  const parentColumn = group.parentColumn;
  const childColumns = group.childColumns;

  const groupHeaderTextRef = useRef<HTMLDivElement>(null);
  const groupHeaderActionsRef = useRef<HTMLDivElement>(null);
  const measurementsRef = useRef({
    contentWidth: 0,
    actionsWidth: 0,
  });

  const isExpanded = isColumnExpanded(parentColumn);
  const actionType = isExpanded ? 'collapse' : 'expand';
  const tooltipText = isExpanded ? 'Collapse column group' : 'Expand column group';

  useLayoutEffect(() => {
    const textElement = groupHeaderTextRef.current;
    const actionsElement = groupHeaderActionsRef.current;

    if (_isNil(textElement) || _isNil(actionsElement)) {
      return;
    }

    const computedStyle = window.getComputedStyle(textElement);
    const marginRight = parseFloat(computedStyle.marginRight) || 0;

    measurementsRef.current = {
      contentWidth: textElement.scrollWidth + marginRight,
      actionsWidth: actionsElement.offsetWidth,
    };
  }, [parentColumn.name]);

  // Apply scroll effect directly to DOM for smooth performance
  useGroupHeaderScroll({
    scrollElementRef,
    targetElementRef: groupHeaderTextRef,
    startOffset,
    groupWidth: width,
    pinnedColumnWidth,
    actionsWidth: measurementsRef.current.actionsWidth,
    contentWidth: measurementsRef.current.contentWidth,
  });

  const handleMoveParentColumn = () => {
    openModal('MOVE_DATATABLE_COLUMN', {
      column: parentColumn,
      tableType: dataTableService.type,
      columns: dataTableService.columns,
      onSave: (update: DataTableColumnMoveRequest) => {
        dataTableService
          .moveColumn(update)
          .then(() => successToast('Column moved Successfully'))
          .then(() => dataTableService.validate())
          .then(() => dataTableService.validateColumn(update.column_id))
          .catch(defaultPromiseErrorHandler);
      },
    });
  };

  const handleDeleteParentColumn = () => {
    openModal('CONFIRM_DELETE_DATA_TABLE_COLUMN', {
      textOverride: 'Deleting this parent column will delete all child columns grouped under it.',
      onClick: () => {
        const columnId = parentColumn?.id;
        if (_notNil(columnId)) {
          dataTableService
            .removeColumn(columnId)
            .then(() => successToast('Parent column and children removed'))
            .catch(defaultPromiseErrorHandler);
        }
      },
      deleteManyColumns: _isNotEmpty(childColumns)
        ? () => (
            <div>
              <span>This parent column and all its child columns will be permanently deleted:</span>
              <ul className="overflow-scroll mt3 ba b--gray" style={{ maxHeight: '150px' }}>
                {childColumns.map((childColumn: DataTableColumn, index: number) => (
                  <li className="bg-light-gray pv2 pl2 tl ba b--moon-gray lh-title f6 near-black" key={index}>
                    {childColumn.name}
                  </li>
                ))}
              </ul>
            </div>
          )
        : null,
    });
  };

  const groupLastColumnIndex = group.startIndex + group.colspan - 1;
  const hasGroupBorder = dataTableService.rightBorderType(groupLastColumnIndex) === 'group-separator';

  return (
    <div
      className={cn(styles.groupHeader, { [styles.groupBorder]: hasGroupBorder })}
      style={{
        left: `${startOffset}px`,
        top: 0,
        width: `${width}px`,
        height: `${dimensions.columnHeight}px`,
      }}
      data-testid={testId}
      data-group-id={parentColumn.id}
    >
      <div ref={groupHeaderTextRef} className={styles.groupHeaderText}>
        {parentColumn.type === 'sample' ? (
          <SampleHeaderContent column={parentColumn} />
        ) : (
          <GroupHeaderContent parentColumn={parentColumn} />
        )}
      </div>
      <div ref={groupHeaderActionsRef} className={styles.groupHeaderActions}>
        <Dropdown
          disabled={dataTableService.readonly}
          renderMenu={() => (
            <span>
              <DropdownMenuItem onClick={handleMoveParentColumn} disabled={parentColumn?.locked ?? false}>
                <span>Move column</span>
              </DropdownMenuItem>
              <DropdownMenuDivider />
              <DropdownMenuItem onClick={handleDeleteParentColumn} disabled={parentColumn?.locked ?? false}>
                <span className="red">Delete</span>
              </DropdownMenuItem>
            </span>
          )}
        >
          <Tooltip render="Parent column options">
            <div
              className={`${styles.columnHeaderDropdown} ${
                dataTableService.readonly ? 'ui__disabled' : ''
              } flex items-center`}
            >
              <RiArrowDropDownFill size={22} />
            </div>
          </Tooltip>
        </Dropdown>
        <Tooltip render={tooltipText}>
          <div
            className={cn('flex items-center ml1', styles.columnHeaderDropdown, {
              ui__disabled: dataTableService.readonly,
            })}
            onClick={() => {
              const action: DataTableColumnGroupExpansionAction = {
                type: actionType,
                column_api_id: parentColumn.id,
              };

              void dataTableService.updateColumnGroupExpansion(action).catch(_noop);
            }}
            data-test-element={`${actionType}-button`}
          >
            <RiArrowLeftRightLine size={18} />
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

const GroupHeaderContent: FC<{ parentColumn: DataTableColumn }> = ({ parentColumn }) => {
  const title = `${parentColumn.type} :: ${parentColumn.name}`;
  const truncationRef = useTruncationTooltip<HTMLDivElement>(title);

  return (
    <>
      <DataTableColumnTypeSymbol
        type={parentColumn.type}
        locked={parentColumn.locked}
        tooltip={renderParentColumnTypeSymbolTooltip(parentColumn)}
      />
      <div className={styles.groupHeaderTextContent}>
        <div className={styles.groupHeaderLabel}>Group</div>
        <div ref={truncationRef} className={styles.groupHeaderTitle}>
          {parentColumn.type} :: {parentColumn.name}
        </div>
      </div>
    </>
  );
};
